import React from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Box, Tab, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as R from 'ramda';

// Components
import Skeletons from './Skeletons';

// styles
const TabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.graphic.light}`,
  display: 'inline-flex',
  marginBottom: theme.spacing(1),
  minHeight: 40,
  zIndex: 2,
}));

const TabsContainerMobile = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.surface.white,
  bottom: 0,
  boxShadow: theme.shadows[0],
  height: 56,
  left: 0,
  margin: 0,
  padding: theme.spacing(0, 3),
  position: 'fixed',
  right: 0,
  width: '100%',
  zIndex: 10,
}));

// Props of importance:
// tabs must be an array of objects
// ex: [{ id: 'baseProducts', name: 'Base Products', shortName: 'Base', icon: 'room_rounded' }, ...]
const ListTabs = ({ activeTab, onChange, pendingState, tabs }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const renderTabs = () =>
    lgUp ? (
      <TabsContainer onChange={R.flip(onChange)} value={activeTab}>
        {tabs.map(({ id, name, shortName }) => (
          <Tab
            key={id}
            label={lgUp ? name : shortName}
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              flexGrow: { xs: 1, lg: 0 },
            }}
            value={id}
          />
        ))}
      </TabsContainer>
    ) : (
      <TabsContainerMobile onChange={R.flip(onChange)} value={activeTab}>
        {tabs.map(({ id, name, shortName }) => (
          <Tab
            key={id}
            label={lgUp ? name : shortName}
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              flexGrow: { xs: 1, lg: 0 },
            }}
            value={id}
          />
        ))}
      </TabsContainerMobile>
    );

  const tabsOrSkeletons = R.ifElse(
    R.both(R.identity, R.equals(lgUp)),
    R.always(<Skeletons />),
    renderTabs
  );

  return tabsOrSkeletons(pendingState);
};

ListTabs.propTypes = {
  activeTab: string,
  onChange: func.isRequired,
  pendingState: bool.isRequired,
  tabs: arrayOf(shape({})).isRequired,
};

ListTabs.defaultProps = {
  activeTab: '',
};

export default ListTabs;
